import React from 'react';

import ItemSelector from '@components/item-selector/ItemSelector';
import SearchableDropdown from '@components/searchable-dropdown/SearchableDropdown';
import EditCategoryModal from '@features/lead-management/components/edit-category-modal/EditCategoryModal';
import useNotify from '@hooks/notify';
import { Category } from 'types/categoryInput';

interface CategoryInputProps {
  value: string;
  onChange: (value: string) => void;
  onCategoryChange: (category: Category | null) => void;
  selectedCategory: Category | null;
  categoryOptions?: Category[];
  onUpdateCategory?: (category: Category) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowCreate?: boolean;
}

// Simple selector for categories using generic ItemSelector
const CategorySelectorWrapper = (props: any) => (
  <ItemSelector<Category>
    {...props}
    showColorSelector={false}
    showEditButton
    createButtonText="Create"
    noItemsText="No categories found"
    EditModalComponent={EditCategoryModal}
    itemType="category"
  />
);

const CategoryInput = ({
  value,
  onChange,
  onCategoryChange,
  selectedCategory,
  categoryOptions = [],
  onUpdateCategory,
  placeholder = 'Select/Search Category',
  className = '',
  disabled = false,
  allowCreate = true
}: CategoryInputProps) => {
  const { createErrorAlert } = useNotify();

  const handleCreateCategory = (categoryName: string): Category => ({
    id: `custom-category-${Date.now()}`,
    name: categoryName
  });

  const handleCategoryChange = (categories: Category[]) => {
    onCategoryChange(categories.length > 0 ? categories[0] : null);
  };

  const validateNameLength = (name: string) => {
    if (name.length < 3 || name.length > 15) {
      createErrorAlert('Category name must be between 3 and 15 characters');
      return false;
    }
    return true;
  };

  const selectedCategories = selectedCategory ? [selectedCategory] : [];

  return (
    <SearchableDropdown<Category>
      value={value}
      onChange={onChange}
      onItemsChange={handleCategoryChange}
      selectedItems={selectedCategories}
      availableItems={categoryOptions}
      onUpdateItem={onUpdateCategory}
      onCreateItem={allowCreate ? handleCreateCategory : undefined}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      allowCreate={allowCreate}
      allowMultiple={false}
      SelectorComponent={CategorySelectorWrapper}
      validateNameLength={validateNameLength}
    />
  );
};

export default CategoryInput;
